import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSeoSchema } from '@/hooks/useSeoSchema';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from './components/HeroSection';
import TreatmentAreasSection from './components/TreatmentAreasSection';
import FeaturedInstitutions from './components/FeaturedInstitutions';
import WhyUsSection from './components/WhyUsSection';
import CtaSection from './components/CtaSection';


const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://healthtravelturkiye.com';

export default function HomePage() {
  const { t, i18n } = useTranslation();

  useSeoSchema(
    'schema-home',
    [
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Health Travel Türkiye',
        url: `${SITE_URL}/`,
        description: 'Discover accredited health institutions in Turkey for patients coming from abroad.',
        potentialAction: { '@type': 'SearchAction', target: `${SITE_URL}/saglik-kurumlari?q={search_term_string}`, 'query-input': 'required name=search_term_string' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Health Travel Türkiye',
        url: `${SITE_URL}/`,
        description: 'Turkey\'s health tourism platform. Guide to accredited health institutions for international patients.',
        areaServed: { '@type': 'Country', name: 'Turkey' },
      },
    ],
    [], // Ana sayfa — sadece "Home" breadcrumb yeterli
    i18n.language,
  );

  useEffect(() => {
    const tc = i18n.getFixedT(i18n.language, 'common');
    document.title = tc('seo_home_title');
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', tc('seo_home_desc'));
    setMeta('keywords', 'health tourism Turkey, hair transplant, dental treatment, aesthetic surgery, IVF, medical tourism');
    setMeta('og:title', tc('seo_home_title'), true);
    setMeta('og:description', tc('seo_home_desc'), true);
    setMeta('og:url', `${SITE_URL}/`, true);
    setMeta('og:image', 'https://public.readdy.ai/ai/img_res/9ec2a3d448699050a4f7fea28c0e161c.jpg', true);
    setMeta('og:image:width', '1200', true);
    setMeta('og:image:height', '630', true);
    setMeta('twitter:image', 'https://readdy.ai/api/search-image?query=Turkey%20health%20tourism%20modern%20hospital%20Istanbul%20Bosphorus%20view%20professional%20medical%20center%20accredited%20healthcare%20facility%20bright%20clean%20interior&width=1200&height=630&seq=og-home-v1&orientation=landscape');
  }, [i18n.language]);

  const steps = [
    {
      color: 'rose',
      icon: 'ri-user-add-line',
      numberKey: 'step1_number',
      titleKey: 'step1_title',
      descKey: 'step1_desc',
      border: 'border-2 border-rose-300',
    },
    {
      color: 'amber',
      icon: 'ri-hospital-line',
      numberKey: 'step2_number',
      titleKey: 'step2_title',
      descKey: 'step2_desc',
      border: 'border-2 border-amber-300',
    },
    {
      color: 'teal',
      icon: 'ri-phone-line',
      numberKey: 'step3_number',
      titleKey: 'step3_title',
      descKey: 'step3_desc',
      border: 'border-2 border-teal-300',
    },
  ] as const;

  return (
    <div className="min-h-screen bg-white font-['Outfit']">
      <Navbar />
      <main>
        <HeroSection />

        {/* Steps */}
        <section className="py-20 bg-gradient-to-br from-rose-50 via-white to-amber-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-['Outfit']">
                {t('steps_title')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('steps_subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
              <div className="hidden md:block absolute top-10 left-1/3 right-1/3 h-0.5 bg-gradient-to-r from-rose-200 via-rose-300 to-rose-200 z-0"></div>

              {steps.map((step) => (
                <div key={step.color} className={`relative z-10 flex flex-col items-center text-center bg-white rounded-2xl p-8 border ${step.border} hover:-translate-y-1 transition-all duration-300`}>
                  <div className={`w-16 h-16 flex items-center justify-center bg-${step.color}-500 rounded-full mb-5 shadow-lg shadow-${step.color}-200`}>
                    <i className={`${step.icon} text-2xl text-white`}></i>
                  </div>
                  <span className={`text-xs font-bold text-${step.color}-400 uppercase tracking-widest mb-2`}>{t(step.numberKey)}</span>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 font-['Outfit']">{t(step.titleKey)}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{t(step.descKey)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <TreatmentAreasSection />
        <FeaturedInstitutions />
        <WhyUsSection />
        <CtaSection />

        {/* Accreditation Band */}
        <section className="bg-white border-t border-gray-100 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-center text-xs font-semibold text-gray-400 uppercase tracking-widest mb-8">
              {t('official_accreditation')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <div className="flex items-center gap-4 bg-gray-50 rounded-2xl px-6 py-4 w-full sm:w-auto">
                <div className="w-20 h-20 flex items-center justify-center flex-shrink-0">
                  <img
                    src="https://storage.readdy-site.link/project_files/b825b40c-ced1-45bc-acd7-e75b219f4e94/d3120357-b604-44a0-aefb-b4527d8be74a_Health-Turkiye-Logo.svg?v=733eda141137079f8cc11e4f506bec91"
                    alt="Health Türkiye"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800 leading-tight">{t('health_turkey')}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{t('health_turkey_desc')}</p>
                </div>
              </div>

              <div className="hidden sm:block w-px h-16 bg-gray-200"></div>

              <div className="flex items-center gap-4 bg-gray-50 rounded-2xl px-6 py-4 w-full sm:w-auto">
                <div className="w-20 h-20 flex items-center justify-center flex-shrink-0">
                  <img
                    src="https://storage.readdy-site.link/project_files/b825b40c-ced1-45bc-acd7-e75b219f4e94/f60d29a1-c69a-4180-b9bf-f3bf3f7fe15d_Republic-of-Turkiye-Ministry-of-Health-Logo.png?v=71e00ccd8f6d40e8d4801bf4a26e4341"
                    alt="Ministry of Health"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800 leading-tight">{t('ministry_health')}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{t('ministry_health_desc')}</p>
                </div>
              </div>

              <div className="hidden sm:block w-px h-16 bg-gray-200"></div>

              <div className="flex items-center gap-4 bg-gray-50 rounded-2xl px-6 py-4 w-full sm:w-auto">
                <div className="w-20 h-20 flex items-center justify-center flex-shrink-0">
                  <img
                    src="https://storage.readdy-site.link/project_files/b825b40c-ced1-45bc-acd7-e75b219f4e94/d8a5e1d4-49c4-4acf-997a-18d6539223bc_Republic-of-Turkiye-Ministry-of-Cultureand-Tourism-Logo.png?v=1f9b9ce043aa351352a5d6d7c5120a9b"
                    alt="Ministry of Culture and Tourism"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800 leading-tight">{t('ministry_culture')}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{t('ministry_culture_desc')}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
